/**
 * @file index.tsx
 *
 * @fileoverview Renders desktop sharing button with sharing options in the popup.
 */
import { SharingButtonProps } from '~/jobiqo/components/SocialMedia/SharingButtons/components/Custom';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { Popover } from '~/vcl/atoms/16-popover';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import ShareIcon from '~/vcl/icons/Share';
import { FormattedMessage } from 'react-intl';
import LinkIcon from '~/vcl/icons/Link';
import CopyButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/CopyButton';
import EmailSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/Email';
import FacebookSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/Facebook';
import LinkedInSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/LinkedIn';
import TwitterSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/Twitter';
import WhatsAppSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/WhatsApp';
import XingSharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/Xing';
import BlueSkySharingButton from '~/jobiqo/components/SocialMedia/SharingButtons/components/BlueSky';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface SharingButtonDesktopProps extends SharingButtonProps {
  /**
   * Full URL of the page to share.
   */
  fullUrl: string;
  /**
   * Flag to indicate if the device is mobile.
   */
  isMobile: boolean;
}

export const SharingButtonDesktop = ({
  fullUrl,
  isMobile,
  ...props
}: SharingButtonDesktopProps) => {
  const { APP } = useFrontendConfig();
  return (
    <Popover
      trigger={
        <Button buttonStyle="primary">
          <ShareIcon
            height="20"
            width="20"
            title={
              <FormattedMessage
                id="social_media_sharing_buttons_icon_text"
                defaultMessage="Share"
              />
            }
            color="white"
          />
          <span>
            <FormattedMessage
              id="social_media_sharing_buttons_label"
              defaultMessage="Share"
            />
          </span>
        </Button>
      }
      triggerAsChild={true}
      position={isMobile ? 'bottom' : 'right'}
    >
      <FlexItems flexDirection="column">
        <Input
          Icon={<LinkIcon color="gray6" title="Share link icon" />}
          iconPosition="start"
          value={fullUrl}
          disabled={true}
          style={{ cursor: 'not-allowed' }}
        />
        <Spacing />
        <CopyButton
          url={fullUrl}
          sharingEntity={props.sharingEntity}
          sharingEntityType={props.sharingEntityType}
        />
        <Spacing />
        {APP.SHARING_BUTTONS_ENABLED.includes('email') && (
          <EmailSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('facebook') && (
          <FacebookSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('linkedin') && (
          <LinkedInSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('twitter') && (
          <TwitterSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('whatsapp') && (
          <WhatsAppSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('xing') && (
          <XingSharingButton {...props} />
        )}
        {APP.SHARING_BUTTONS_ENABLED.includes('bluesky') && (
          <BlueSkySharingButton {...props} />
        )}
      </FlexItems>
    </Popover>
  );
};

export default SharingButtonDesktop;
